import { useState, useEffect, useMemo, useCallback } from 'react';
import { breakpoints } from '../styles/theme';

interface ResponsiveState {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLarge: boolean;
  isExtraLarge: boolean;
  orientation: 'portrait' | 'landscape';
  breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
}

interface ResponsiveUtilities extends ResponsiveState {
  // 반응형 값 선택 유틸리티
  responsiveValue: <T>(values: Partial<Record<keyof typeof breakpoints | 'base', T>>) => T;
  // 그리드 컬럼 수 계산
  getGridCols: (options?: { mobile?: number; tablet?: number; desktop?: number }) => number;
  // 컨테이너 패딩 계산
  getContainerPadding: () => number;
  // 폰트 크기 스케일링
  getFontScale: () => number;
  // 컴포넌트 크기 계산
  getComponentSize: () => 'small' | 'middle' | 'large';
  // 사이드바 표시 여부
  shouldShowSidebar: () => boolean;
  // 테이블 스크롤 필요 여부
  needsTableScroll: () => boolean;
}

// 디바이스별 설정
const deviceConfigs = {
  mobile: {
    maxWidth: breakpoints.md,
    gridCols: 1,
    padding: 12,
    fontSize: 0.9,
    componentSize: 'small' as const,
  },
  tablet: {
    maxWidth: breakpoints.lg,
    gridCols: 2,
    padding: 16,
    fontSize: 1,
    componentSize: 'middle' as const,
  },
  desktop: {
    maxWidth: Infinity,
    gridCols: 3,
    padding: 24,
    fontSize: 1,
    componentSize: 'middle' as const,
  },
};

/**
 * 향상된 반응형 훅
 * 
 * 다양한 디바이스와 화면 크기에 대응하는 반응형 유틸리티를 제공합니다.
 * 성능 최적화와 함께 실용적인 헬퍼 함수들을 포함합니다.
 */
export const useResponsiveImproved = (): ResponsiveUtilities => {
  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  // 디바운스된 리사이즈 핸들러
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    setWindowSize(prev => {
      // 변화가 없으면 업데이트하지 않음 (리렌더링 방지)
      if (prev.width === width && prev.height === height) {
        return prev;
      }
      return { width, height };
    });
  }, []);

  useEffect(() => {
    // 디바운스 타이머
    let timeoutId: NodeJS.Timeout;
    
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    handleResize(); // 초기값 설정

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [handleResize]);

  // 반응형 상태 계산 (메모이제이션)
  const responsiveState: ResponsiveState = useMemo(() => {
    const { width, height } = windowSize;
    
    // 브레이크포인트 계산
    let breakpoint: ResponsiveState['breakpoint'] = 'xs';
    if (width >= breakpoints.xxl) breakpoint = 'xxl';
    else if (width >= breakpoints.xl) breakpoint = 'xl';
    else if (width >= breakpoints.lg) breakpoint = 'lg';
    else if (width >= breakpoints.md) breakpoint = 'md';
    else if (width >= breakpoints.sm) breakpoint = 'sm';

    return {
      width,
      height,
      isMobile: width < breakpoints.md,
      isTablet: width >= breakpoints.md && width < breakpoints.lg,
      isDesktop: width >= breakpoints.lg,
      isLarge: width >= breakpoints.xl,
      isExtraLarge: width >= breakpoints.xxl,
      orientation: height > width ? 'portrait' : 'landscape',
      breakpoint,
    };
  }, [windowSize]);

  // 반응형 값 선택 유틸리티
  const responsiveValue = useCallback(<T>(
    values: Partial<Record<keyof typeof breakpoints | 'base', T>>
  ): T => {
    const { breakpoint } = responsiveState;
    
    // 현재 브레이크포인트부터 역순으로 찾기
    const breakpointOrder: (keyof typeof values)[] = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs', 'base'];
    const currentIndex = breakpointOrder.indexOf(breakpoint);
    
    for (let i = currentIndex; i < breakpointOrder.length; i++) {
      const key = breakpointOrder[i];
      if (values[key] !== undefined) {
        return values[key] as T;
      }
    }
    
    // 기본값 반환
    return values.base || values.xs || Object.values(values)[0];
  }, [responsiveState]);

  // 그리드 컬럼 수 계산
  const getGridCols = useCallback((options?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
  }) => {
    const { isMobile, isTablet } = responsiveState;
    
    if (isMobile) return options?.mobile ?? deviceConfigs.mobile.gridCols;
    if (isTablet) return options?.tablet ?? deviceConfigs.tablet.gridCols;
    return options?.desktop ?? deviceConfigs.desktop.gridCols;
  }, [responsiveState]);

  // 컨테이너 패딩 계산
  const getContainerPadding = useCallback(() => {
    const { isMobile, isTablet } = responsiveState;
    
    if (isMobile) return deviceConfigs.mobile.padding;
    if (isTablet) return deviceConfigs.tablet.padding;
    return deviceConfigs.desktop.padding;
  }, [responsiveState]);

  // 폰트 크기 스케일링
  const getFontScale = useCallback(() => {
    const { isMobile, isTablet } = responsiveState;
    
    if (isMobile) return deviceConfigs.mobile.fontSize;
    if (isTablet) return deviceConfigs.tablet.fontSize;
    return deviceConfigs.desktop.fontSize;
  }, [responsiveState]);

  // 컴포넌트 크기 계산
  const getComponentSize = useCallback(() => {
    const { isMobile, isTablet } = responsiveState;
    
    if (isMobile) return deviceConfigs.mobile.componentSize;
    if (isTablet) return deviceConfigs.tablet.componentSize;
    return deviceConfigs.desktop.componentSize;
  }, [responsiveState]);

  // 사이드바 표시 여부
  const shouldShowSidebar = useCallback(() => {
    return responsiveState.width >= breakpoints.lg;
  }, [responsiveState.width]);

  // 테이블 스크롤 필요 여부
  const needsTableScroll = useCallback(() => {
    return responsiveState.width < breakpoints.lg;
  }, [responsiveState.width]);

  return {
    ...responsiveState,
    responsiveValue,
    getGridCols,
    getContainerPadding,
    getFontScale,
    getComponentSize,
    shouldShowSidebar,
    needsTableScroll,
  };
};

// CSS-in-JS용 반응형 스타일 생성기
export const createResponsiveStyles = (
  styles: Partial<Record<keyof typeof breakpoints, React.CSSProperties>>
): React.CSSProperties => {
  const mediaQueries: Record<string, React.CSSProperties> = {};
  
  Object.entries(breakpoints).forEach(([key, value]) => {
    const breakpointKey = key as keyof typeof breakpoints;
    if (styles[breakpointKey]) {
      mediaQueries[`@media (min-width: ${value}px)`] = styles[breakpointKey]!;
    }
  });

  return mediaQueries;
};

// 반응형 그리드 시스템
export const useResponsiveGrid = () => {
  const { width, getGridCols } = useResponsiveImproved();

  const getColSpan = useCallback((spans: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xxl?: number;
  }) => {
    if (width >= breakpoints.xxl && spans.xxl) return spans.xxl;
    if (width >= breakpoints.xl && spans.xl) return spans.xl;
    if (width >= breakpoints.lg && spans.lg) return spans.lg;
    if (width >= breakpoints.md && spans.md) return spans.md;
    if (width >= breakpoints.sm && spans.sm) return spans.sm;
    return spans.xs || 24;
  }, [width]);

  const getGutter = useCallback((): [number, number] => {
    if (width < breakpoints.sm) return [8, 8];
    if (width < breakpoints.md) return [12, 12];
    if (width < breakpoints.lg) return [16, 16];
    return [24, 24];
  }, [width]);

  return {
    getColSpan,
    getGutter,
    getGridCols,
  };
};

// 반응형 타이포그래피
export const useResponsiveTypography = () => {
  const { isMobile, isTablet, getFontScale } = useResponsiveImproved();
  const scale = getFontScale();

  const getFontSize = useCallback((base: number) => {
    return Math.round(base * scale);
  }, [scale]);

  const getHeadingLevel = useCallback((desktopLevel: 1 | 2 | 3 | 4 | 5) => {
    if (isMobile && desktopLevel < 3) {
      return Math.min(desktopLevel + 1, 5) as 1 | 2 | 3 | 4 | 5;
    }
    return desktopLevel;
  }, [isMobile]);

  return {
    getFontSize,
    getHeadingLevel,
    scale,
    isMobile,
    isTablet,
  };
};